/**
 * cycleCardLabels — the label lines shown on a Cycles-tab card. Spec §5.
 *
 * Pure function: takes the cycle fields the cycles API already returns plus the
 * viewer's current time, and returns the strings (and whether Reopen should be
 * offered) for CycleCard to render. Keeping the date math here (rather than in the
 * component) makes it testable without rendering, and keeps CycleCard from
 * re-deriving the 30-day reopen window — that rule lives on the server only
 * (see reopenableUntil in the /cycles response).
 */
import { format } from 'date-fns';

type PersonName = { firstName: string; lastName: string };

export interface CycleForLabels {
  status: 'upcoming' | 'active' | 'closed';
  closedAt?: string | null;
  closedBy?: PersonName | null;
  reopenedAt?: string | null;
  reopenedBy?: PersonName | null;
  reopenableUntil?: string | null;
}

export interface CycleCardLabels {
  closedLine?: string;
  reopenLine?: string;
  reopenedLine?: string;
  canReopen: boolean;
}

const DATE_FORMAT = 'd MMM yyyy';
const fullName = (person: PersonName) => `${person.firstName} ${person.lastName}`;

export function cycleCardLabels(cycle: CycleForLabels, now: Date): CycleCardLabels {
  const labels: CycleCardLabels = { canReopen: false };

  if (cycle.status === 'closed') {
    // closedLine needs closedAt. Legacy cycles (closed before this release) were never
    // backfilled with closedAt by the migration, so they simply show no closed line.
    if (cycle.closedAt) {
      const closedDate = format(new Date(cycle.closedAt), DATE_FORMAT);
      labels.closedLine = cycle.closedBy
        ? `Closed by ${fullName(cycle.closedBy)} on ${closedDate}`
        : `Closed automatically on ${closedDate}`;
    }

    // reopenLine/canReopen must NOT depend on closedAt: the server derives reopenableUntil
    // for legacy cycles too (falling back to updatedAt — spec §4.4) and the reopen endpoint
    // supports them, so gating this on closedAt would wrongly hide Reopen for every cycle
    // closed before this release for the first 30 days after it ships.
    if (cycle.reopenableUntil) {
      const reopenableUntil = new Date(cycle.reopenableUntil);
      if (reopenableUntil.getTime() > now.getTime()) {
        labels.canReopen = true;
        labels.reopenLine = `Can reopen until ${format(reopenableUntil, DATE_FORMAT)}`;
      } else {
        labels.reopenLine = 'Reopen period ended';
      }
    }
  }

  if (cycle.reopenedAt && cycle.reopenedBy) {
    labels.reopenedLine = `Reopened by ${fullName(cycle.reopenedBy)} on ${format(
      new Date(cycle.reopenedAt),
      DATE_FORMAT
    )}`;
  }

  return labels;
}
